/**
 * Category Master - Form Screen (Add/Edit)
 * Uses erp-theme.css classes ONLY. No inline styles.
 * Live MSSQL data via /api/category-master
 */
import React, { useState, useEffect, useCallback, memo } from 'react';
import { Save, Grid3X3, XCircle, Search, Plus, Minus } from 'lucide-react';
import ERPLookupModal from '../../../components/erp/ERPLookupModal';
import ERPActionColumn from '../../../components/erp/ERPActionColumn';
import DynamicFieldRenderer from '../../../components/erp/DynamicFieldRenderer';
import { erpToast } from '../../../components/erp/ERPToast';
import '../../../styles/erp-theme.css';
import { API_BASE as API_URL } from '../../../lib/api';


// Enter → Next Field navigation (global for all ERP forms)
const handleEnterNav = (e) => {
  if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') {
    e.preventDefault();
    const form = e.target.closest('form');
    if (!form) return;
    const els = Array.from(form.querySelectorAll('input:not([disabled]):not([readonly]),select:not([disabled]),textarea:not([disabled])'));
    const idx = els.indexOf(e.target);
    if (idx >= 0 && idx < els.length - 1) els[idx + 1].focus();
  }
};

// STABLE components — defined OUTSIDE render to prevent remount
const ERPInput = memo(({ label, value, onChange, required, disabled, testId, readOnly: ro }) => (
  <div className="erp-form-row">
    <label className={`erp-form-label${required ? ' required' : ''}`}>{label}</label>
    <div className="erp-form-control">
      <input value={value || ''} onChange={(e) => onChange(e.target.value)} disabled={disabled || ro}
        className="erp-input" data-testid={testId} onKeyDown={handleEnterNav} />
    </div>
  </div>
));

const ERPLookupField = memo(({ label, value, displayValue, required, onSearch, onAdd, testId, readOnly: ro }) => (
  <div className="erp-form-row">
    <label className={`erp-form-label${required ? ' required' : ''}`}>{label}</label>
    <div className="erp-form-control">
      <input value={displayValue || value || ''} readOnly placeholder="Search Here" className="erp-input"
        onClick={!ro ? onSearch : undefined} data-testid={testId} disabled={ro}
        style={{ cursor: ro ? 'not-allowed' : 'pointer' }} onKeyDown={handleEnterNav} />
      {!ro && <button className="erp-btn-icon" onClick={onSearch} tabIndex={-1}><Search size={13} /></button>}
      {!ro && onAdd && <span className="erp-lookup-add" onClick={onAdd} data-testid={`${testId}-add`}>+</span>}
    </div>
  </div>
));

const CategoryMasterForm = ({ token, editData, mode = 'add', onGridView, onSaved }) => {
  const [activeTab, setActiveTab] = useState('category');
  const [showMore, setShowMore] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [recentData, setRecentData] = useState([]);
  const [lookupConfig, setLookupConfig] = useState(null);
  const [dynValues, setDynValues] = useState({});

  // Get user role from localStorage
  const userRole = (() => { try { const u = JSON.parse(localStorage.getItem('retailcliq_user') || '{}'); return u.role || ''; } catch { return ''; } })();
  const isSuperAdmin = userRole === 'SUPER_ADMIN' || userRole === 'admin' || userRole === 'SYS_SUPERUSER' || userRole === 'ADMINSUPPORT' || userRole === 'LICENSOR';

  const emptyForm = {
    CatId: '', CatName: '', CatSname: '', CategoryType: 'G', HSNCode: '', SACCode: '', HSNID: '',
    DepartmentId: '', DepartmentName: '', UOMId: '', UOMName: '',
    OEMReqOrNot: false, Inventory: false, ExpDays: 0, Desc5: '', Desc6: '',
    Relation: '', ParentCategory: '', ParentCategoryName: '', Conversion: ''
  };
  const [form, setForm] = useState(emptyForm);
  const isView = mode === 'view';

  useEffect(() => { if (editData && mode !== 'add') setForm({ ...emptyForm, ...editData, DepartmentId: editData.Desc1 || '', DepartmentName: editData.Desc2 || '' }); }, [editData, mode]);

  const loadRecent = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/category-master?page=1&pageSize=10`, { headers: { Authorization: `Bearer ${token}` } });
      const json = await res.json();
      if (json.success) setRecentData(json.data || []);
    } catch {}
  }, [token]);
  useEffect(() => { loadRecent(); }, [loadRecent]);

  const handleSave = async () => {
    if (!form.CatName?.trim()) { setError('Category Name is required'); return; }
    setSaving(true); setError('');
    try {
      const method = mode === 'edit' ? 'PUT' : 'POST';
      const url = mode === 'edit' ? `${API_URL}/api/category-master/${form.CatId}` : `${API_URL}/api/category-master`;
      const res = await fetch(url, { method, headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(form) });
      const json = await res.json();
      if (json.success) {
        // Save dynamic field values
        const catId = json.data?.CatId || form.CatId;
        if (catId && Object.keys(dynValues).length > 0) {
          await fetch(`${API_URL}/api/dynamic-fields/values`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ screen: 'CategoryMaster', recordId: catId, values: dynValues }) });
        }
        erpToast.success('Saved successfully!'); handleClear(); loadRecent(); onSaved?.();
      }
      else setError(json.error || 'Save failed');
    } catch { setError('Save failed'); }
    setSaving(false);
  };

  const handleClear = () => { setForm(emptyForm); setActiveTab('category'); setShowMore(false); setError(''); setDynValues({}); };
  const set = (n, v) => setForm(p => ({ ...p, [n]: v }));

  const fetchDepts = async ({ search }) => { const r = await fetch(`${API_URL}/api/category-master/lookup/departments?search=${search||''}`, { headers: { Authorization: `Bearer ${token}` } }); const j = await r.json(); return { data: j.data||[], total: j.data?.length||0 }; };
  const fetchHSN = async ({ search, page=1, pageSize=10 }) => { const r = await fetch(`${API_URL}/api/category-master/lookup/hsncodes?search=${search||''}&page=${page}&pageSize=${pageSize}`, { headers: { Authorization: `Bearer ${token}` } }); return await r.json(); };
  const fetchUOM = async ({ search }) => { const r = await fetch(`${API_URL}/api/category-master/lookup/uoms?search=${search||''}`, { headers: { Authorization: `Bearer ${token}` } }); const j = await r.json(); return { data: j.data||[], total: j.data?.length||0 }; };
  const fetchCats = async ({ search }) => { const r = await fetch(`${API_URL}/api/category-master?search=${search||''}&page=1&pageSize=20`, { headers: { Authorization: `Bearer ${token}` } }); const j = await r.json(); return { data: j.data||[], total: j.total||0 }; };

  return (
    <div className="erp-module" data-testid="category-master-form">
      <div className="erp-screen-header">
        <span className="erp-screen-title">Category Master</span>
        {/* Record ID Display — Previous ID + New ID */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, fontSize: 12, fontFamily: 'var(--erp-font)' }}>
          {form.CatId ? (
            <span style={{ color: 'var(--erp-text-secondary)' }}>ID: <strong style={{ color: 'var(--erp-primary)', fontFamily: 'var(--erp-font-mono)' }}>{form.CatId}</strong></span>
          ) : (
            <>
              <span style={{ color: 'var(--erp-text-muted)' }}>Previous ID: <strong style={{ fontFamily: 'var(--erp-font-mono)' }}>{recentData[0]?.CatId || '—'}</strong></span>
              <span style={{ color: 'var(--erp-text-secondary)' }}>New ID: <strong style={{ color: 'var(--erp-warning)', fontFamily: 'var(--erp-font-mono)' }}>Auto-generated</strong></span>
            </>
          )}
        </div>
        <div className="erp-screen-actions">
          {!isView && <button className="erp-btn erp-btn-ghost" onClick={handleSave} disabled={saving} data-testid="save-btn"><Save size={14} /> {saving ? 'Saving...' : (mode === 'edit' ? 'Update' : 'Save')}</button>}
          <button className="erp-btn erp-btn-ghost" onClick={onGridView} data-testid="gridview-btn"><Grid3X3 size={14} /> Gridview</button>
          <button className="erp-btn erp-btn-ghost" onClick={handleClear} data-testid="clear-btn"><XCircle size={14} /> Clear</button>
          {isSuperAdmin && !isView && <button className="erp-btn erp-btn-primary" onClick={() => { const el = document.querySelector('[data-testid="add-dynamic-field-btn"]'); if (el) el.click(); else { const sec = document.querySelector('[data-testid="dynamic-fields-section"]'); if (sec) sec.scrollIntoView({ behavior: 'smooth' }); } }} data-testid="header-add-field-btn"><Plus size={14} /> Add Field</button>}
        </div>
      </div>

      {error && <div className="erp-alert-error">{error}</div>}

      <form style={{flex:1,overflow:'auto',padding:'var(--erp-sp-4)'}} onSubmit={(e) => e.preventDefault()}>
        <div className="erp-form-card">
          <div className="erp-tabs">
            <button className={`erp-tab${activeTab === 'category' ? ' active' : ''}`} onClick={() => setActiveTab('category')}>Category</button>
            <button className={`erp-tab${activeTab === 'groups' ? ' active' : ''}`} onClick={() => setActiveTab('groups')}>Groups</button>
          </div>

          <div className="erp-form-section">
            {activeTab === 'category' && (
              <>
                <ERPLookupField readOnly={isView} label="Department" value={form.DepartmentId} displayValue={form.DepartmentName}
                  onSearch={() => setLookupConfig({ title: 'Department Master', columns: [{field:'SDMName',header:'SDM NAME'},{field:'SDMUId',header:'SDM UID'}], fetch: fetchDepts, onSelect: (r) => { set('DepartmentId',r.SDMUId); set('DepartmentName',r.SDMName); set('Desc1',r.SDMUId); set('Desc2',r.SDMName); setLookupConfig(null); } })}
                  onAdd={() => erpToast.info('Add Department - coming soon')} testId="dept-lookup" />

                <div className="erp-form-2col">
                  <div>
                    <ERPInput readOnly={isView} label="Category Name" value={form.CatName} onChange={(v) => set('CatName',v)} required testId="input-CatName" />
                    <ERPLookupField readOnly={isView} label="HSN CODE" value={form.HSNCode} required
                      onSearch={() => setLookupConfig({ title: 'HSN Code Master', columns: [{field:'HSNCode',header:'HSN Code'},{field:'Category',header:'Category'},{field:'HSNId',header:'HSN ID'}], fetch: fetchHSN, onSelect: (r) => { set('HSNCode',r.HSNCode); set('HSNID',r.HSNId); setLookupConfig(null); } })}
                      onAdd={() => erpToast.info('Add HSN Code - coming soon')} testId="hsn-lookup" />
                  </div>
                  <div>
                    <div className="erp-form-row">
                      <label className="erp-form-label">Category Type(Code</label>
                      <div className="erp-form-control">
                        <select value={form.CategoryType} onChange={(e) => set('CategoryType',e.target.value)} disabled={isView} className="erp-input erp-select" data-testid="select-CategoryType">
                          <option value="G">Lot Wise</option><option value="P">Piece Wise</option><option value="W">Weight Wise</option>
                        </select>
                      </div>
                    </div>
                    <ERPLookupField readOnly={isView} label="UOM" value={form.UOMId} displayValue={form.UOMName}
                      onSearch={() => setLookupConfig({ title: 'UOM Master', columns: [{field:'UOMName',header:'UOM NAME'},{field:'UOMId',header:'UOM ID'}], fetch: fetchUOM, onSelect: (r) => { set('UOMId',r.UOMId); set('UOMName',r.UOMName); setLookupConfig(null); } })}
                      onAdd={() => erpToast.info('Add UOM - coming soon')} testId="uom-lookup" />
                    <ERPLookupField readOnly={isView} label="SAC CODE" value={form.SACCode} onSearch={() => erpToast.info('SAC Code lookup - coming soon')} onAdd={() => erpToast.info('Add SAC Code - coming soon')} testId="sac-lookup" />
                  </div>
                </div>

                <button className="erp-collapse-toggle" onClick={() => setShowMore(!showMore)} data-testid="toggle-more">
                  {showMore ? <Minus size={14} /> : <Plus size={14} />} More
                </button>

                {showMore && (
                  <div className="erp-form-2col" style={{marginTop:'var(--erp-sp-3)'}}>
                    <div>
                      <ERPInput readOnly={isView} label="Category Short Name" value={form.CatSname} onChange={(v) => set('CatSname',v)} testId="input-CatSname" />
                      <div className="erp-form-row" style={{alignItems:'flex-start'}}>
                        <label className="erp-form-label" style={{paddingTop:6}}>Language Text</label>
                        <div className="erp-form-control">
                          <textarea value={form.Desc5||''} onChange={(e) => set('Desc5',e.target.value)} rows={3} disabled={isView} className="erp-input" data-testid="input-LanguageText" />
                        </div>
                      </div>
                    </div>
                    <div>
                      <label className="erp-checkbox"><input type="checkbox" checked={form.OEMReqOrNot} onChange={(e) => set('OEMReqOrNot',e.target.checked)} disabled={isView} /> OEM Req</label>
                      <label className="erp-checkbox"><input type="checkbox" checked={form.Inventory} onChange={(e) => set('Inventory',e.target.checked)} disabled={isView} /> ScanQty Edit</label>
                      <label className="erp-checkbox"><input type="checkbox" disabled /> Use as manual with separate line</label>
                      <label className="erp-checkbox"><input type="checkbox" checked={form.ExpDays > 0} onChange={(e) => set('ExpDays',e.target.checked ? 30 : 0)} disabled={isView} /> Expiry Days Required</label>
                      <span style={{fontSize:'var(--erp-text-sm)',color:'var(--erp-text-muted)'}}>Serial No. / IMEI Code</span>
                    </div>
                  </div>
                )}
                <p className="erp-note">Note : Inventory &gt;&gt; Requirement &gt;&gt; Serial No / IMEI Code Updation</p>
                <p className="erp-note">Use above path to upload the Serial Nos by using downloaded file</p>
              </>
            )}

            {activeTab === 'groups' && (
              <>
                <div className="erp-form-2col">
                  <div>
                    <div className="erp-form-row">
                      <label className="erp-form-label">Relation</label>
                      <div className="erp-form-control">
                        <select value={form.Relation||''} onChange={(e) => set('Relation',e.target.value)} disabled={isView} className="erp-input erp-select" data-testid="select-Relation">
                          <option value="">--Select--</option><option value="Parent">Parent</option><option value="Child">Child</option>
                        </select>
                      </div>
                    </div>
                    <ERPInput readOnly={isView} label="Conversion" value={form.Conversion} onChange={(v) => set('Conversion',v)} testId="input-Conversion" />
                  </div>
                  <div>
                    <ERPLookupField readOnly={isView} label="Parent Category" value={form.ParentCategory} displayValue={form.ParentCategoryName}
                      onSearch={() => setLookupConfig({ title: 'Category Master', columns: [{field:'CatName',header:'Category Name'},{field:'CatId',header:'Category ID'}], fetch: fetchCats, onSelect: (r) => { set('ParentCategory',r.CatId); set('ParentCategoryName',r.CatName); setLookupConfig(null); } })}
                      onAdd={() => erpToast.info('Add Category - coming soon')} testId="parentcat-lookup" />
                  </div>
                </div>

                <button className="erp-collapse-toggle" onClick={() => setShowMore(!showMore)}>
                  {showMore ? <Minus size={14} /> : <Plus size={14} />} More
                </button>
                {showMore && (
                  <div className="erp-form-2col" style={{marginTop:'var(--erp-sp-3)'}}>
                    <div>
                      <ERPInput readOnly={isView} label="Category Short Name" value={form.CatSname} onChange={(v) => set('CatSname',v)} testId="input-CatSname2" />
                      <div className="erp-form-row" style={{alignItems:'flex-start'}}>
                        <label className="erp-form-label" style={{paddingTop:6}}>Language Text</label>
                        <div className="erp-form-control"><textarea value={form.Desc5||''} onChange={(e) => set('Desc5',e.target.value)} rows={3} disabled={isView} className="erp-input" /></div>
                      </div>
                    </div>
                    <div>
                      <label className="erp-checkbox"><input type="checkbox" checked={form.OEMReqOrNot} onChange={(e) => set('OEMReqOrNot',e.target.checked)} disabled={isView} /> OEM Req</label>
                      <label className="erp-checkbox"><input type="checkbox" checked={form.Inventory} onChange={(e) => set('Inventory',e.target.checked)} disabled={isView} /> ScanQty Edit</label>
                      <label className="erp-checkbox"><input type="checkbox" disabled /> Use as manual with separate line</label>
                      <label className="erp-checkbox"><input type="checkbox" checked={form.ExpDays > 0} onChange={(e) => set('ExpDays',e.target.checked ? 30 : 0)} disabled={isView} /> Expiry Days Required</label>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Dynamic Fields — rendered from DynamicFieldsConfig metadata */}
        <DynamicFieldRenderer screenName="CategoryMaster" recordId={form.CatId} token={token}
          values={dynValues} onChange={(key, val) => setDynValues(p => ({...p, [key]: val}))} readOnly={isView} userRole={userRole} />

        {/* Embedded Grid — header fixed, only tbody scrolls */}
        <div className="erp-embedded-grid">
          <div className="erp-grid-wrapper">
            {/* Fixed header table */}
            <table className="erp-grid erp-grid-header-fixed">
              <thead>
                <tr>
                  <th style={{width:50}}>Actions</th>
                  <th>Category ID</th>
                  <th>Category Name</th>
                  <th>Type</th>
                  <th>HSN Code</th>
                </tr>
              </thead>
            </table>
            {/* Scrollable body */}
            <div className="erp-grid-body-scroll">
              <table className="erp-grid">
                <tbody>
                  {recentData.map((row) => (
                    <tr key={row.CatId}>
                      <td style={{width:50}}>
                        <ERPActionColumn row={row}
                          onView={() => setForm({...emptyForm,...row})}
                          onEdit={() => setForm({...emptyForm,...row})}
                          onVoid={() => erpToast.warning(`Void ${row.CatId}`)}
                          onPrint={() => window.print()}
                          onExport={() => erpToast.info(`Export ${row.CatId}`)}
                        />
                      </td>
                      <td className="cell-mono">{row.CatId}</td>
                      <td className="cell-bold">{row.CatName}</td>
                      <td>{row.CategoryTypeDisplay}</td>
                      <td className="cell-mono">{row.HSNCode}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </form>

      {lookupConfig && (
        <ERPLookupModal title={lookupConfig.title} columns={lookupConfig.columns} fetchData={lookupConfig.fetch}
          onSelect={lookupConfig.onSelect} onClose={() => setLookupConfig(null)} onAdd={() => erpToast.info('Add new - coming soon')} />
      )}
    </div>
  );
};

export default CategoryMasterForm;
